import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { createFamilyMember, getFamilyRelationshipTypes, processSuggestionsBulk } from '../../services/api'
import { transformPayload } from '../../utils/familyApiTransform'
import { axiosInstance } from '../../services/api'
import Layout from '../common/Layout'
import SuggestionConfirmModal from './SuggestionConfirmModal'
import { getUser } from '../../utils/secureStorage'

function AddChildScreen() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    relationship_type: '',
    parent_id: ''
  })

  const [relationshipTypes, setRelationshipTypes] = useState([])
  const [parents, setParents] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingRelationships, setIsLoadingRelationships] = useState(true)
  const [isLoadingParents, setIsLoadingParents] = useState(true)
  const [error, setError] = useState('')
  const [calculatedAge, setCalculatedAge] = useState(null)

  // Suggestion modal state
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false)
  const [isProcessingSuggestions, setIsProcessingSuggestions] = useState(false)
  const [newChildName, setNewChildName] = useState('')

  // Child relationship types — gendered so we can infer the child's sex
  const childRelationshipTypes = [
    { id: 'son', name: 'Son', reverse_name: 'Parent' },
    { id: 'daughter', name: 'Daughter', reverse_name: 'Parent' },
    { id: 'step-son', name: 'Step-Son', reverse_name: 'Step-Parent' },
    { id: 'step-daughter', name: 'Step-Daughter', reverse_name: 'Step-Parent' },
  ]

  useEffect(() => {
    fetchRelationshipTypes()
    fetchPotentialParents()
  }, [])

  const fetchRelationshipTypes = async () => {
    try {
      setIsLoadingRelationships(true)
      // For children, we can use predefined types or fetch from API
      setRelationshipTypes(childRelationshipTypes)
    } catch (err) {
      console.error('Error fetching relationship types:', err)
      setRelationshipTypes(childRelationshipTypes)
    } finally {
      setIsLoadingRelationships(false)
    }
  }

  const fetchPotentialParents = async () => {
    try {
      setIsLoadingParents(true)
      const familyId = localStorage.getItem('familyId')
      if (!familyId) {
        throw new Error('No family ID found')
      }

      const response = await axiosInstance.get(`/families/${familyId}/members/`)
      const membersData = response.data.members || response.data.results || response.data || []

      // Filter to show all adults (not just those with accounts)
      // Any adult family member can have children added under them
      const potentialParents = membersData
        .filter(member => !member.is_minor)
        .map(member => ({
          id: member.id,
          name: `${member.first_name} ${member.last_name}`.trim(),
          relationship: member.role || 'Family member',
          hasAccount: member.has_account
        }))

      setParents(potentialParents)
    } catch (err) {
      console.error('Error fetching potential parents:', err)
    } finally {
      setIsLoadingParents(false)
    }
  }

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) {
      setCalculatedAge(null)
      return
    }

    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    setCalculatedAge(age)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (name === 'date_of_birth') {
      calculateAge(value)
    }

    if (error) setError('')
  }

  const handleDateBlur = (e) => {
    const value = e.target.value
    if (value) {
      const year = parseInt(value.split('-')[0], 10)
      // If year is invalid (e.g., 0000 or before 1900), clear the field
      if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
        setFormData(prev => ({
          ...prev,
          date_of_birth: ''
        }))
        setCalculatedAge(null)
        setError('Please enter a valid date (year must be between 1900 and today)')
      }
    }
  }

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError("Child's first name is required")
      return false
    }

    if (!formData.relationship_type) {
      setError('Please select a relationship type')
      return false
    }

    if (!formData.parent_id) {
      setError('Please select a parent')
      return false
    }

    return true
  }

  const inferGenderFromRelationship = (relationshipName) => {
    // Infer gender from gendered relationship types (including step- variants)
    const lowerName = relationshipName.toLowerCase()

    if (lowerName.includes('son') && !lowerName.includes('daughter')) {
      return 'male'  // son, step-son
    } else if (lowerName.includes('daughter')) {
      return 'female'  // daughter, step-daughter
    }

    return 'unknown'
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    // Use the selected parent_id, not the logged-in user's ID
    // The child should be added to the SELECTED parent
    const selectedParentId = formData.parent_id
    if (!selectedParentId) {
      setError('Please select a parent for the child.')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const selectedRelationType = relationshipTypes.find(type => type.id === formData.relationship_type)
      if (!selectedRelationType) {
        throw new Error('Invalid relationship type selected')
      }

      // Infer gender from relationship type if not already set
      const inferredGender = formData.gender || inferGenderFromRelationship(selectedRelationType.name)

      const legacyPayload = {
        human: {
          email: null, // Children don't have email
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          middle_name: null,
          date_of_birth: formData.date_of_birth || null,
          city_of_birth: null,
          country_of_birth: null,
          gender: inferredGender,  // Use inferred gender
          is_minor: true // Always true for this form
        },
        relationship_type: {
          name: selectedRelationType.name,
          reverse_name: selectedRelationType.reverse_name || selectedRelationType.name
        },
        communication_initiated: false,
        parent_id: formData.parent_id // Include parent ID
      }

      // Use selectedParentId (the person the child is being added to)
      // NOT the logged-in user's ID
      const newFormatPayload = transformPayload(legacyPayload, selectedParentId)
      const result = await createFamilyMember(newFormatPayload)

      // Check for inferred relationship suggestions
      if (result.inferred_suggestions && result.inferred_suggestions.length > 0) {
        setNewChildName(formData.first_name)
        setSuggestions(result.inferred_suggestions)
        setShowSuggestionsModal(true)
      } else {
        // No suggestions - navigate directly
        navigate('/family-hub?child_added=true')
      }

    } catch (err) {
      console.error('Error adding child:', err)
      setError(err.message || 'Failed to add child. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptSuggestions = async ({ accept, reject }) => {
    setIsProcessingSuggestions(true)
    try {
      const result = await processSuggestionsBulk({ accept, reject })
      if (result.errors && result.errors.length > 0) {
        console.error('Errors during suggestion processing:', result.errors)
      }
      setShowSuggestionsModal(false)
      navigate('/family-hub?child_added=true')
    } catch (err) {
      console.error('Error processing suggestions:', err)
      // Still navigate even if suggestions fail
      navigate('/family-hub?child_added=true')
    } finally {
      setIsProcessingSuggestions(false)
    }
  }

  const handleSkipSuggestions = () => {
    setShowSuggestionsModal(false)
    navigate('/family-hub?child_added=true')
  }

  const getUserPersonId = () => {
    try {
      const userData = getUser() || {}
      return userData.person_id || userData.id || null
    } catch (error) {
      return null
    }
  }

  return (
    <Layout>
      {/* Main Content */}
      <div className="min-h-full px-6 md:px-10 py-8" style={{ background: '#F5F5DC' }}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">👶 Add a Child</h1>
          <p className="text-lg text-gray-600">Add a child under 18 through their parent</p>
        </div>

        <div className="max-w-2xl">
            {/* Info Box */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5 mb-6 flex gap-4">
              <span className="text-3xl">💡</span>
              <div>
                <div className="text-lg font-bold text-blue-900 mb-1">About Adding Children</div>
                <div className="text-base text-blue-800">
                  Children under 18 don't need their own account. They connect through their parent and can see your stories when their parent shows them.
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Row */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-2">
                    Child's First Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="Enter child's name"
                    className="w-full px-5 py-4 border-3 border-gray-200 rounded-xl text-lg focus:outline-none focus:border-green-600"
                    required
                  />
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-2">
                    Child's Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    className="w-full px-5 py-4 border-3 border-gray-200 rounded-xl text-lg focus:outline-none focus:border-green-600"
                  />
                </div>
              </div>

              {/* Relationship and DOB Row */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-2">
                    Relationship to You <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="relationship_type"
                    value={formData.relationship_type}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 border-3 border-gray-200 rounded-xl text-lg bg-white focus:outline-none focus:border-green-600"
                    required
                  >
                    <option value="">Select relationship...</option>
                    {relationshipTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        My {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-2">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    onBlur={handleDateBlur}
                    min="1900-01-01"
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-5 py-4 border-3 border-gray-200 rounded-xl text-lg focus:outline-none focus:border-green-600"
                  />
                  <div className="text-base text-gray-600 mt-2">Optional - for birthday greetings</div>
                  {calculatedAge !== null && (
                    <div className="text-base text-blue-700 font-medium mt-1">
                      Age: {calculatedAge} years old
                    </div>
                  )}
                </div>
              </div>

              {/* Parent Selection */}
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-2">
                  Their Parent <span className="text-red-600">*</span>
                </label>
                <select
                  name="parent_id"
                  value={formData.parent_id}
                  onChange={handleInputChange}
                  className="w-full px-5 py-4 border-3 border-gray-200 rounded-xl text-lg bg-white focus:outline-none focus:border-green-600"
                  required
                >
                  <option value="">Select which parent...</option>
                  {parents.map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name} ({parent.relationship}){!parent.hasAccount ? ' - In tree only' : ''}
                    </option>
                  ))}
                </select>
                <div className="text-base text-gray-600 mt-2">
                  This child will be connected through this parent's account
                </div>
              </div>

              {/* Warning Box */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 flex gap-4">
                <span className="text-3xl">📬</span>
                <div>
                  <div className="text-lg font-bold text-amber-900 mb-1">Parent Will Be Notified</div>
                  <div className="text-base text-amber-800">
                    The parent you select will receive a notification to confirm adding this child.
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-5 py-4 rounded-xl text-base">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold text-white transition-all disabled:opacity-50"
                  style={{ background: '#556B2F' }}
                >
                  👶 Add Child
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/family-hub')}
                  className="px-8 py-4 rounded-2xl text-lg font-bold bg-white border-3 border-gray-300 text-gray-900 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Suggestion Confirmation Modal */}
        {showSuggestionsModal && (
          <SuggestionConfirmModal
            suggestions={suggestions}
            newPersonName={newChildName}
            onAccept={handleAcceptSuggestions}
            onSkip={handleSkipSuggestions}
            isLoading={isProcessingSuggestions}
          />
        )}
      </Layout>
    )
}

export default AddChildScreen
